import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
  DatePicker,
} from 'antd';
import {
  WarningOutlined,
  ReloadOutlined,
  LinkOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import managementExceptionsService, {
  MANAGEMENT_EXCEPTION_TYPE_OPTIONS,
  type ManagementExceptionCode,
  type ManagementExceptionItem,
  type ManagementExceptionSeverity,
} from '@/services/managementExceptions.service';
import { useAuthStore } from '@/store/authStore';

const { Title, Paragraph, Text } = Typography;

const severityColor: Record<ManagementExceptionSeverity, string> = {
  critical: 'red',
  warning: 'orange',
  info: 'blue',
};

const ExceptionsDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = (searchParams.get('type') || '') as ManagementExceptionCode | '';
  const severityFilter = (searchParams.get('severity') || '') as ManagementExceptionSeverity | '';
  const [leaseOpen, setLeaseOpen] = useState(false);
  const [leaseForm] = Form.useForm();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManageLeases = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('management.exceptions.leases.manage') ||
      user?.permissions?.includes('company.view')
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['management-exceptions', typeFilter],
    queryFn: () =>
      managementExceptionsService.getExceptions({
        codes: typeFilter || undefined,
        limit: 100,
      }),
    refetchInterval: 5 * 60 * 1000,
  });

  const leasesQuery = useQuery({
    queryKey: ['facility-leases'],
    queryFn: () => managementExceptionsService.listFacilityLeases(),
    enabled: leaseOpen,
  });

  const upsertLease = useMutation({
    mutationFn: managementExceptionsService.upsertFacilityLease,
    onSuccess: () => {
      message.success('Facility lease saved');
      queryClient.invalidateQueries({ queryKey: ['facility-leases'] });
      queryClient.invalidateQueries({ queryKey: ['management-exceptions'] });
      leaseForm.resetFields();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  const deleteLease = useMutation({
    mutationFn: managementExceptionsService.deleteFacilityLease,
    onSuccess: () => {
      message.success('Lease removed');
      queryClient.invalidateQueries({ queryKey: ['facility-leases'] });
      queryClient.invalidateQueries({ queryKey: ['management-exceptions'] });
    },
  });

  const items = useMemo(() => {
    let list = data?.items || [];
    if (severityFilter) list = list.filter((i) => i.severity === severityFilter);
    return list;
  }, [data?.items, severityFilter]);

  const columns = [
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 110,
      render: (s: ManagementExceptionSeverity) => <Tag color={severityColor[s]}>{s.toUpperCase()}</Tag>,
    },
    {
      title: 'Type',
      dataIndex: 'code',
      key: 'code',
      width: 220,
      render: (code: ManagementExceptionCode) =>
        MANAGEMENT_EXCEPTION_TYPE_OPTIONS.find((o) => o.value === code)?.label || code,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Detail',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
    {
      title: 'Amount / days',
      key: 'metrics',
      width: 130,
      render: (_: unknown, row: ManagementExceptionItem) => {
        if (row.days != null) return `${row.days}d`;
        if (row.amount != null) return Number(row.amount).toLocaleString(undefined, { maximumFractionDigits: 2 });
        return '—';
      },
    },
    {
      title: '',
      key: 'link',
      width: 80,
      render: (_: unknown, row: ManagementExceptionItem) =>
        row.link ? (
          <Link to={row.link}>
            <Button type="link" size="small" icon={<LinkOutlined />}>
              Open
            </Button>
          </Link>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <WarningOutlined style={{ marginRight: 8 }} />
            Management Exceptions
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Credit, AR/AP, inventory, project budget, material usage, lease expiry, purchase price, GRN billing, and
            three-way match exceptions in one workbench.
          </Paragraph>
        </div>
        <Space>
          {canManageLeases && (
            <Button icon={<HomeOutlined />} onClick={() => setLeaseOpen(true)}>
              Facility leases
            </Button>
          )}
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
            Refresh
          </Button>
        </Space>
      </Space>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={(error as Error).message || 'Failed to load exceptions'}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Total open" value={data?.total ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Critical" value={data?.by_severity?.critical ?? 0} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Warning" value={data?.by_severity?.warning ?? 0} valueStyle={{ color: '#d48806' }} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            style={{ minWidth: 260 }}
            value={typeFilter}
            options={MANAGEMENT_EXCEPTION_TYPE_OPTIONS}
            onChange={(v) => {
              const next = new URLSearchParams(searchParams);
              if (v) next.set('type', v);
              else next.delete('type');
              setSearchParams(next);
            }}
          />
          <Select
            style={{ minWidth: 160 }}
            value={severityFilter}
            allowClear
            placeholder="Severity"
            options={[
              { value: 'critical', label: 'Critical' },
              { value: 'warning', label: 'Warning' },
              { value: 'info', label: 'Info' },
            ]}
            onChange={(v) => {
              const next = new URLSearchParams(searchParams);
              if (v) next.set('severity', v);
              else next.delete('severity');
              setSearchParams(next);
            }}
          />
          {data?.as_of && (
            <Text type="secondary">As of {dayjs(data.as_of).format('YYYY-MM-DD HH:mm')}</Text>
          )}
        </Space>
      </Card>

      {(data?.by_type || []).length > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {data!.by_type.map((t) => (
            <Col key={t.code} xs={24} sm={12} md={8} lg={6}>
              <Card
                size="small"
                hoverable
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set('type', t.code);
                  setSearchParams(next);
                }}
              >
                <Text strong>{t.label}</Text>
                <div>
                  <Tag>{t.count}</Tag>
                  {t.critical > 0 && <Tag color="red">{t.critical} critical</Tag>}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card>
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={items}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          size="middle"
        />
      </Card>

      <Modal
        title="Facility leases"
        open={leaseOpen}
        onCancel={() => setLeaseOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Paragraph type="secondary">
          Facility / office leases are not covered by asset warranty or PMV hire. Add them here so expiry alerts appear
          in the workbench.
        </Paragraph>
        <Form
          form={leaseForm}
          layout="vertical"
          onFinish={(values) =>
            upsertLease.mutate({
              lease_name: values.lease_name,
              facility_reference: values.facility_reference,
              lessor_name: values.lessor_name,
              start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
              end_date: values.end_date.format('YYYY-MM-DD'),
              notice_days: values.notice_days ?? 60,
              annual_rent: values.annual_rent,
              notes: values.notes,
              status: 'Active',
            })
          }
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="lease_name" label="Lease name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="facility_reference" label="Facility reference">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lessor_name" label="Lessor">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="notice_days" label="Notice days" initialValue={60}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="start_date" label="Start date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="end_date" label="End date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="annual_rent" label="Annual rent">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="notes" label="Notes">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" loading={upsertLease.isPending}>
            Add lease
          </Button>
        </Form>

        <Table
          style={{ marginTop: 16 }}
          size="small"
          rowKey="id"
          loading={leasesQuery.isLoading}
          dataSource={leasesQuery.data || []}
          pagination={false}
          columns={[
            { title: 'Name', dataIndex: 'lease_name' },
            { title: 'End', dataIndex: 'end_date', width: 120 },
            { title: 'Notice', dataIndex: 'notice_days', width: 80 },
            {
              title: '',
              width: 90,
              render: (_: unknown, row: { id: string }) => (
                <Button danger type="link" size="small" onClick={() => deleteLease.mutate(row.id)}>
                  Remove
                </Button>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default ExceptionsDashboard;
